import { FC, JSX, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { BoardItem } from '@cloudscape-design/board-components';
import Board, { BoardProps } from '@cloudscape-design/board-components/board';
import {
  Box,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  KeyValuePairs,
  Link,
  SpaceBetween,
} from '@cloudscape-design/components';

import { Timestamp } from '@firebase/firestore';
import { RiCheckboxCircleFill } from 'react-icons/ri';
import { subscribeToBatchesByStatus } from '../api/batches';
import { fetchItems, fetchItemsWithFilter, Filter } from '../api/generic';
import Flex from '../components/Flex';
import Loader from '../components/Loader';
import { HydroPiSample, iSpindelSample } from '../components/modules/HydroPi';
import { useAuthContext } from '../contexts/AuthContext';
import { useModuleTranslation } from '../lang';
import { Batch } from '../models/Batch';
import { Brew } from '../models/Brew';
import Tenant from '../models/Tenant';
import { exportLayout, StoredWidgetPlacement } from '../utils/layout';
import { prefixCollection } from '../utils/prefix';
import CalendarWidget from '../widgets/CalendarWidget';
import ISpindelWidget from '../widgets/ISpindelWidget';
import NextStepsWidget from '../widgets/NextStepsWidget';
import TiltsWidget from '../widgets/TiltsWidget';
import { boardI18nStrings, boardItemI18nStrings } from './i18n-strings';

const dataInit = { id: '' };

type WidgetId = 'batchOverview' | 'nextSteps' | 'calendar' | 'tilts' | 'ispindels' | 'default';

const defaultLayouts: ReadonlyArray<BoardProps.Item<StoredWidgetPlacement>> = [
  {
    id: 'batchOverview',
    columnOffset: { 4: 0 },
    columnSpan: 2,
    rowSpan: 3,
    data: dataInit,
  },
  {
    id: 'nextSteps',
    columnOffset: { 4: 2 },
    columnSpan: 2,
    rowSpan: 3,
    data: dataInit,
  },
  {
    id: 'calendar',
    columnOffset: { 4: 0 },
    columnSpan: 4,
    rowSpan: 7,
    data: dataInit,
  },
  {
    id: 'tilts',
    columnOffset: { 4: 2 },
    columnSpan: 2,
    rowSpan: 2,
    data: dataInit,
  },
  {
    id: 'ispindels',
    columnOffset: { 4: 2 },
    columnSpan: 2,
    rowSpan: 2,
    data: dataInit,
  },
];

const Home: FC = () => {
  const { tenant, currentUser, department } = useAuthContext();
  const [tenantItem, setTenantItem] = useState<Tenant>();
  const [batches, setBatches] = useState<Batch[]>();
  const [tilts, setTilts] = useState<HydroPiSample[]>();
  const [iSpindels, setISpindels] = useState<iSpindelSample[]>();
  const [layout, setLayout] = useLocalStorage<ReadonlyArray<StoredWidgetPlacement>>('home-layout', defaultLayouts);
  const [brews, setBrews] = useState<Brew[]>();
  const t = useModuleTranslation('Home');

  const addMissingLayoutItems = (layout: ReadonlyArray<StoredWidgetPlacement>) => {
    const missingItems = defaultLayouts.filter((defaultItem) => !layout.find((item) => item.id === defaultItem.id));
    const newLayout = [...layout, ...missingItems.map((item) => ({ ...item, data: dataInit }))];
    return newLayout;
  };

  function handleLayoutChange(layout: ReadonlyArray<StoredWidgetPlacement>) {
    const newLayout = addMissingLayoutItems(layout);
    setLayout(newLayout);
  }

  const fetchTenant = async () => {
    const filteredItems = await fetchItemsWithFilter<Tenant>('tenant', [
      {
        field: 'name',
        operator: '==',
        value: tenant,
      },
    ]);

    setTenantItem(filteredItems[0]);
  };

  const fetchTiltData = async () => {
    const filterLast48h: Filter<HydroPiSample>[] = [
      {
        field: 'timestamp',
        operator: '>',
        value: Timestamp.fromDate(new Date(Date.now() - 48 * 60 * 60 * 1000)),
      },
    ];
    const tileData = await fetchItemsWithFilter<HydroPiSample>(prefixCollection('hydroPi', tenant, '-'), filterLast48h);

    const groupTiltsByColor = tileData.reduce(
      (acc, tilt) => {
        if (!acc[tilt.color]) {
          acc[tilt.color] = [];
        }
        acc[tilt.color].push(tilt);
        return acc;
      },
      {} as Record<string, HydroPiSample[]>,
    );

    const latestRecordForEachColor = Object.keys(groupTiltsByColor).map(
      (color) => groupTiltsByColor[color].sort((a, b) => b.timestamp.seconds - a.timestamp.seconds)[0],
    );
    setTilts(latestRecordForEachColor);
  };

  const fetchISpindelData = async () => {
    const filterLast48h: Filter<iSpindelSample>[] = [
      {
        field: 'timestamp',
        operator: '>',
        value: Timestamp.fromDate(new Date(Date.now() - 48 * 60 * 60 * 1000)),
      },
    ];
    const iSpindelData = await fetchItemsWithFilter<iSpindelSample>(
      prefixCollection('ispindel', tenant, '-'),
      filterLast48h,
    );

    const groupISpindelsByColor = iSpindelData.reduce(
      (acc, iSpindel) => {
        if (!acc[iSpindel.color]) {
          acc[iSpindel.color] = [];
        }
        acc[iSpindel.color].push(iSpindel);
        return acc;
      },
      {} as Record<string, iSpindelSample[]>,
    );

    const latestRecordForEachColor = Object.keys(groupISpindelsByColor).map(
      (color) => groupISpindelsByColor[color].sort((a, b) => b.timestamp.seconds - a.timestamp.seconds)[0],
    );

    setISpindels(latestRecordForEachColor);
  };

  const fetchBrews = async () => {
    const activeBrews = await fetchItems<Brew>(prefixCollection('brews', tenant));

    setBrews(activeBrews);
  };

  useEffect(() => {
    fetchBrews();
    fetchTenant();
    fetchTiltData();
    fetchISpindelData();
    if (layout) handleLayoutChange(layout);
  }, [tenant]);

  useEffect(() => {
    const unsubscribe = subscribeToBatchesByStatus(['planned', 'in-progress'], tenant, (batches) =>
      setBatches(batches),
    );
    return unsubscribe;
  }, []);

  const totalActiveBatches = useMemo(
    () =>
      batches?.filter(
        (batch) => batch.status === 'in-progress' && (batch.department === department || department === ''),
      ).length ?? 0,
    [batches, department],
  );

  const plannedBatches = useMemo(
    () =>
      batches?.filter((batch) => batch.status === 'planned' && (batch.department === department || department === ''))
        .length ?? 0,
    [batches, department],
  );
  const dataForLayout: Record<
    WidgetId,
    {
      title: string;
      description: string;
      header: () => JSX.Element;
      content: () => JSX.Element;
    }
  > = {
    default: {
      title: t.default.title,
      description: t.default.description,
      header: () => <Header>{t.default.header}</Header>,
      content: () => <></>,
    },
    nextSteps: {
      title: t.nextSteps.title,
      description: t.nextSteps.description,
      header: () => <Header>{t.nextSteps.header}</Header>,
      content: () => (
        <Loader isLoading={!batches}>
          <NextStepsWidget batches={batches?.filter((b) => b.department === department || department === '') ?? []} />
        </Loader>
      ),
    },
    calendar: {
      title: t.calendar.title,
      description: t.calendar.description,
      header: () => <Header>{t.calendar.header}</Header>,
      content: () => (
        <Loader isLoading={!batches}>
          <CalendarWidget batches={batches?.filter((b) => b.department === department || department === '') ?? []} />
        </Loader>
      ),
    },
    batchOverview: {
      title: t.batchOverview.title,
      description: t.batchOverview.description,
      header: () => (
        <Header
        // actions={
        //   <Button
        //     variant="primary"
        //     iconName="arrow-right"
        //     iconAlign="right"
        //     onClick={() => {
        //       window.location.href = '/brew';
        //     }}
        //   >
        //     {t.batchOverview.startBrewing}
        //   </Button>
        // }
        >
          {t.batchOverview.header}
        </Header>
      ),
      content: () => (
        <KeyValuePairs
          columns={4}
          items={[
            {
              label: t.batchOverview.active,
              value: (
                <Link variant="awsui-value-large" href="/batches">
                  {totalActiveBatches}
                </Link>
              ),
            },
            {
              label: t.batchOverview.planned,
              value: (
                <Header>
                  <Link variant="awsui-value-large" href="/planning">
                    {plannedBatches}
                  </Link>
                </Header>
              ),
            },
            {
              label: brews?.length ? (
                <span style={{ whiteSpace: 'nowrap' }}>
                  <Link href={`/brew/${brews[0].id}`} variant="primary">
                    {brews[0].name}
                  </Link>
                </span>
              ) : (
                <Box variant="awsui-key-label" color="inherit">
                  {t.batchOverview.noBrews}
                </Box>
              ),
              value:
                (brews?.length ?? 0) > 1 ? (
                  <SpaceBetween direction="vertical" size="xs">
                    {(brews ?? []).slice(1).map((brew) => (
                      <span key={brew.id} style={{ whiteSpace: 'nowrap' }}>
                        <Link href={`/brew/${brew.id}`} variant="info">
                          {brew.name}
                        </Link>
                      </span>
                    ))}
                  </SpaceBetween>
                ) : (
                  <></>
                ),
            },
          ]}
        />
      ),
    },
    tilts: {
      title: `${t.tilts.title} (${tilts?.length})`,
      description: t.tilts.description,
      header: () => <Header>{t.tilts.header}</Header>,
      content: () =>
        tilts ? (
          <TiltsWidget tilts={tilts} batches={batches?.filter((b) => b.status === 'in-progress') ?? []} />
        ) : (
          <></>
        ),
    },
    ispindels: {
      title: `${t.ispindels.title} (${iSpindels?.length})`,
      description: t.ispindels.description,
      header: () => <Header>{t.ispindels.header}</Header>,
      content: () =>
        iSpindels ? (
          <ISpindelWidget
            iSpindels={iSpindels}
            batches={batches?.filter((b) => b.status === 'in-progress') ?? []}
          ></ISpindelWidget>
        ) : (
          <></>
        ),
    },
  };

  const mappedLayout = layout
    ? layout.map((item) => ({
        ...item,
        data: dataForLayout[item.id as WidgetId] ?? dataForLayout.default,
      }))
    : [];

  return (
    <ContentLayout disableOverlap header={<Header variant="h1" description={`${tenantItem?.displayName ?? ''}`} />}>
      {!currentUser ? (
        <Flex column>
          <Box textAlign="center" padding="xl">
            <Flex align="center">
              <img src="/logo_2.png" alt="Producery logo" style={{ height: 100, marginBottom: 20 }} />
              <Box variant="h1" fontWeight="bold" padding="s">
                The all-in-one mead platform made for brewers, by brewers
              </Box>
            </Flex>
            <Box variant="p" padding={{ top: 's' }} fontSize="body-m">
              Say goodbye to spreadsheets. Producery gives you batch planning, inventory, invoicing, customs compliance,
              and more — in one place.
            </Box>
            <Box margin={{ top: 'l' }}>{/* <Button variant="primary">Start for Free</Button> */}</Box>
          </Box>

          {/* PROBLEM/SOLUTION */}
          <Container header={<Header variant="h2">Why Producery?</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <Box>
                <Box variant="h3">Tired of spreadsheets?</Box>
                <Box variant="p">
                  No more digging through notebooks or Excel. Producery centralizes your brewing operations.
                </Box>
              </Box>
              <Box>
                <Box variant="h3">Compliance, handled</Box>
                <Box variant="p">
                  Built with Dutch customs requirements in mind, including audit trail and excise tracking.
                </Box>
              </Box>
            </ColumnLayout>
          </Container>

          {/* FEATURE HIGHLIGHTS */}
          <Container header={<Header variant="h2">Key Features</Header>}>
            <ColumnLayout columns={3}>
              <Box>
                <RiCheckboxCircleFill /> Recipe management
              </Box>
              <Box>
                <RiCheckboxCircleFill /> Batch calculator & ABV estimator
              </Box>
              <Box>
                <RiCheckboxCircleFill /> Inventory tracking
              </Box>
              <Box>
                <RiCheckboxCircleFill /> Invoice uploads with linking
              </Box>
              <Box>
                <RiCheckboxCircleFill /> Full audit trail
              </Box>
              <Box>
                <RiCheckboxCircleFill /> API integrations (webshop, etc.)
              </Box>
            </ColumnLayout>
          </Container>

          {/* PRICING */}
          <Container header={<Header variant="h2">Pricing</Header>}>
            <ColumnLayout columns={2}>
              <Box>
                <Box variant="h3">Producery Basic</Box>
                <Box variant="p">Free forever. Includes recipes, batch planning, inventory, ABV tools.</Box>
                {/* <Button variant="normal">Get Started</Button> */}
              </Box>
              <Box>
                <Box variant="h3">Producery Plus – €20/month</Box>
                <Box variant="p">Adds invoicing, stock, audit tracking, API integrations, and support.</Box>
                {/* <Button variant="primary">Upgrade to Plus</Button> */}
              </Box>
            </ColumnLayout>
          </Container>
        </Flex>
      ) : (
        <div style={{ marginTop: 10 }}>
          <Board
            items={mappedLayout}
            i18nStrings={boardI18nStrings}
            empty={t.board.empty}
            onItemsChange={({ detail: { items } }) => {
              handleLayoutChange(exportLayout(items));
            }}
            renderItem={(item) => (
              <BoardItem
                i18nStrings={boardItemI18nStrings}
                header={item.data.header ? <item.data.header /> : <Header>{t.board.itemDefaultHeader}</Header>}
              >
                {item.data.content ? <item.data.content /> : t.board.itemDefaultContent}
              </BoardItem>
            )}
          />
        </div>
      )}
    </ContentLayout>
  );
};

export default Home;
